import { useState } from 'react';

const SavingsCalculator = () => {
  const [monthlyBill, setMonthlyBill] = useState<string>('');
  const [displayBill, setDisplayBill] = useState<string>('');
  const [contractPower, setContractPower] = useState<string>('');
  const [displayContractPower, setDisplayContractPower] = useState<string>('');
  const [maxLoad, setMaxLoad] = useState<string>('');
  const [displayMaxLoad, setDisplayMaxLoad] = useState<string>('');
  const [isCalculated, setIsCalculated] = useState(false);
  const [animateResults, setAnimateResults] = useState(false);

  // 숫자를 한글 금액으로 변환
  const formatKoreanNumber = (num: number): string => {
    if (num === 0) return '';
    
    const units = ['', '만', '억', '조'];
    const parts: string[] = [];
    let remaining = num;
    let unitIndex = 0;
    
    while (remaining > 0) {
      const part = remaining % 10000;
      if (part > 0) {
        parts.unshift(`${part.toLocaleString('ko-KR')}${units[unitIndex]}`);
      }
      remaining = Math.floor(remaining / 10000);
      unitIndex++;
    }
    
    return parts.join(' ') + '원';
  };

  // 입력값 포맷팅 (콤마 추가)
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (value: string) => void,
    displaySetter: (value: string) => void
  ) => {
    const rawValue = e.target.value.replace(/,/g, '');
    
    if (rawValue === '' || /^\d+$/.test(rawValue)) {
      setter(rawValue);
      
      if (rawValue) {
        const numValue = parseInt(rawValue, 10);
        displaySetter(numValue.toLocaleString('ko-KR'));
      } else {
        displaySetter('');
      }
      
      if (isCalculated) {
        setIsCalculated(false);
        setAnimateResults(false);
      }
    }
  };

  const calculateSavings = () => {
    const billInWon = parseFloat(monthlyBill) * 10000; // 만원 -> 원
    const contract = parseFloat(contractPower) || 0;
    const load = parseFloat(maxLoad) || 0;

    if (billInWon <= 0 || (contract <= 0 && load <= 0)) return;
    
    setIsCalculated(true);
    setTimeout(() => setAnimateResults(true), 100);
  };

  const resetCalculator = () => {
    setIsCalculated(false);
    setAnimateResults(false);
    setMonthlyBill('');
    setDisplayBill('');
    setContractPower('');
    setDisplayContractPower('');
    setMaxLoad('');
    setDisplayMaxLoad('');
  };

  // 계산 로직
  const billInWon = parseFloat(monthlyBill) * 10000 || 0; // 만원 -> 원
  const contract = parseFloat(contractPower) || 0;
  const load = parseFloat(maxLoad) || 0;
  
  const savingsRate = 0.04; // 4% 고정
  
  // 필요 설치 용량 계산 (계약전력 * 부하율 45% 또는 최대전력의 80%)
  const loadFactor = 0.45; // 부하율 45% (40~50% 중간값)
  const maxLoadFactor = 0.8; // 최대전력의 80%
  
  let requiredCapacity = 0;
  if (contract > 0 && load > 0) {
    // 둘 다 입력된 경우, 두 계산 중 큰 값 사용
    requiredCapacity = Math.max(contract * loadFactor, load * maxLoadFactor);
  } else if (contract > 0) {
    // 계약전력만 입력된 경우
    requiredCapacity = contract * loadFactor;
  } else if (load > 0) {
    // 최대부하만 입력된 경우
    requiredCapacity = load * maxLoadFactor;
  }
  
  // 설치 대수 (올림)
  const installCount = Math.ceil(requiredCapacity / 100);
  
  // 총 투자금 (만원)
  const totalInvestment = installCount * 700;
  
  // 월 절감액 (원)
  const monthlySavings = Math.round(billInWon * savingsRate);
  
  // 예상 회수 개월
  const paybackMonths = monthlySavings > 0 ? Math.round((totalInvestment * 10000) / monthlySavings) : 0;
  
  // 연간 절감액
  const yearlySavings = monthlySavings * 12;
  
  // 10년간 총 절감액
  const tenYearSavings = yearlySavings * 10;
  
  // 투자 적합성 판단
  const getRecommendation = () => {
    if (paybackMonths <= 18) {
      return {
        status: 'excellent',
        message: '매우 우수 (18개월 이내 회수 가능)',
        color: 'emerald'
      };
    }
    
    if (paybackMonths <= 30) {
      return {
        status: 'reasonable',
        message: '합리적 (19~30개월 회수 예상)',
        color: 'amber'
      };
    }
    
    if (paybackMonths <= 36) {
      return {
        status: 'careful',
        message: '신중히 검토 필요 (31~36개월 회수 예상)',
        color: 'orange'
      };
    }
    
    return {
      status: 'not-recommended',
      message: '투자 회수 기간이 길어 추천하지 않습니다 (37개월 이상)',
      color: 'red'
    };
  };

  const recommendation = getRecommendation();

  const formatNumber = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-white to-amber-50/30">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-flex items-center gap-2 px-3 md:px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-xs md:text-sm font-medium mb-3 md:mb-4">
            <i className="ri-calculator-line text-base md:text-lg"></i>
            절감 계산기
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4 px-4">
            SEPIS 설치 시 <span className="text-amber-500">예상 절감액</span>을 확인하세요
          </h2>
          <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto px-4">
            현재 월 전기요금과 계약전력 정보를 입력하시면 SEPIS 설치 후 예상되는 절감 금액과&nbsp; 투자 회수 기간을 바로 확인하실 수 있습니다.
          </p>
          <p className="text-gray-500 text-xs md:text-sm max-w-2xl mx-auto mt-2 px-4">
            (단, 100kVA 절전기 설치시 기준이며, 보수적으로 4%의 절감률 기준으로 산출한 결과임)
          </p>
        </div>

        {/* Calculator Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Input Section */}
            <div className="p-5 md:p-8 lg:p-10 bg-gradient-to-r from-gray-900 to-gray-800">
              <div className="space-y-5 md:space-y-6">
                {/* 월 전기요금 입력 */}
                <div>
                  <label className="block text-amber-400 text-xs md:text-sm font-medium mb-2">
                    월 평균 전기요금 (한전 고지서 기준)
                  </label>
                  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={displayBill}
                        onChange={(e) => handleInputChange(e, setMonthlyBill, setDisplayBill)}
                        placeholder="예: 5,000"
                        className="w-full pl-4 pr-16 py-3 md:py-4 text-lg md:text-xl font-semibold bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-base md:text-lg">만원</span>
                    </div>
                    {parseFloat(monthlyBill) > 0 && (
                      <div className="md:min-w-[140px] text-left md:text-right">
                        <span className="text-amber-400 text-base md:text-lg font-medium">
                          {formatKoreanNumber(parseFloat(monthlyBill) * 10000)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 계약전력 / 최대부하 입력 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-amber-400 text-xs md:text-sm font-medium mb-2">
                      계약전력 (선택)
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={displayContractPower}
                        onChange={(e) => handleInputChange(e, setContractPower, setDisplayContractPower)}
                        placeholder="예: 500"
                        className="w-full pl-4 pr-16 py-3 md:py-4 text-lg md:text-xl font-semibold bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-base md:text-lg">kW</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-amber-400 text-xs md:text-sm font-medium mb-2">
                      최대부하 (선택)
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={displayMaxLoad}
                        onChange={(e) => handleInputChange(e, setMaxLoad, setDisplayMaxLoad)}
                        placeholder="예: 450"
                        className="w-full pl-4 pr-16 py-3 md:py-4 text-lg md:text-xl font-semibold bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-base md:text-lg">kVA</span>
                    </div>
                  </div>
                </div>

                <p className="text-gray-400 text-xs">
                  * 계약전력 또는 최대부하 중 하나 이상을 입력해주세요. 산업용 전력 기준입니다.
                </p>

                {/* 버튼 */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  {isCalculated ? (
                    <button
                      onClick={resetCalculator}
                      className="whitespace-nowrap px-6 py-3 md:py-4 bg-gray-600 hover:bg-gray-500 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <i className="ri-refresh-line"></i>
                      다시 계산
                    </button>
                  ) : (
                    <button
                      onClick={calculateSavings}
                      disabled={!monthlyBill || parseFloat(monthlyBill) <= 0 || (parseFloat(contractPower) <= 0 && parseFloat(maxLoad) <= 0)}
                      className="whitespace-nowrap px-6 md:px-8 py-3 md:py-4 bg-amber-500 hover:bg-amber-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30 cursor-pointer"
                    >
                      <i className="ri-calculator-line text-lg md:text-xl"></i>
                      절감액 계산하기
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Results Section */}
            {isCalculated && (
              <div className="p-5 md:p-8 lg:p-10">
                {/* 투자 적합성 판단 */}
                <div className={`mb-6 md:mb-8 p-4 md:p-6 rounded-xl border-2 transition-all duration-500 ${animateResults ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${
                  recommendation.color === 'emerald' ? 'bg-emerald-50 border-emerald-300' :
                  recommendation.color === 'amber' ? 'bg-amber-50 border-amber-300' :
                  recommendation.color === 'orange' ? 'bg-orange-50 border-orange-300' :
                  'bg-red-50 border-red-300'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      recommendation.color === 'emerald' ? 'bg-emerald-500' :
                      recommendation.color === 'amber' ? 'bg-amber-500' :
                      recommendation.color === 'orange' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`}>
                      <i className={`text-xl md:text-2xl text-white ${
                        recommendation.color === 'emerald' ? 'ri-check-line' :
                        recommendation.color === 'amber' ? 'ri-information-line' :
                        recommendation.color === 'orange' ? 'ri-alert-line' :
                        'ri-close-line'
                      }`}></i>
                    </div>
                    <div>
                      <p className={`text-sm md:text-lg font-bold ${
                        recommendation.color === 'emerald' ? 'text-emerald-900' :
                        recommendation.color === 'amber' ? 'text-amber-900' :
                        recommendation.color === 'orange' ? 'text-orange-900' :
                        'text-red-900'
                      }`}>
                        {recommendation.message}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Main Savings Display */}
                <div className={`text-center mb-8 md:mb-10 transition-all duration-500 delay-100 ${animateResults ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <p className="text-gray-500 mb-2 text-sm md:text-base">SEPIS 설치 시 예상 월 절감액 (절감률 4% 기준)</p>
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-amber-500">
                      {formatNumber(monthlySavings)}
                    </span>
                    <span className="text-xl md:text-2xl text-gray-600 font-medium">원</span>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 transition-all duration-500 delay-200 ${animateResults ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  {/* 필요 설치 용량 */}
                  <div className="bg-gradient-to-br from-sky-50 to-sky-100/50 rounded-xl p-4 md:p-6 border border-sky-200/50">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-sky-500 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                      <i className="ri-flashlight-line text-xl md:text-2xl text-white"></i>
                    </div>
                    <p className="text-gray-600 text-xs md:text-sm mb-1">필요 설치 용량</p>
                    <p className="text-lg md:text-2xl font-bold text-gray-900">
                      {formatNumber(Math.round(requiredCapacity))}<span className="text-sm md:text-lg font-medium text-gray-600">kVA</span>
                    </p>
                    <p className="text-sky-600 text-xs mt-1">{installCount}대 설치 필요</p>
                  </div>

                  {/* 총 투자금 */}
                  <div className="bg-gradient-to-br from-violet-50 to-violet-100/50 rounded-xl p-4 md:p-6 border border-violet-200/50">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-violet-500 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                      <i className="ri-money-dollar-circle-line text-xl md:text-2xl text-white"></i>
                    </div>
                    <p className="text-gray-600 text-xs md:text-sm mb-1">총 투자금</p>
                    <p className="text-lg md:text-2xl font-bold text-gray-900">
                      {formatNumber(totalInvestment)}<span className="text-sm md:text-lg font-medium text-gray-600">만원</span>
                    </p>
                    <p className="text-violet-600 text-xs mt-1">대당 700만원 기준</p>
                  </div>

                  {/* Payback Period */}
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-4 md:p-6 border border-emerald-200/50">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-500 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                      <i className="ri-time-line text-xl md:text-2xl text-white"></i>
                    </div>
                    <p className="text-gray-600 text-xs md:text-sm mb-1">예상 투자 회수 기간</p>
                    <p className="text-lg md:text-2xl font-bold text-gray-900">
                      약 {paybackMonths}<span className="text-sm md:text-lg font-medium text-gray-600">개월</span>
                    </p>
                    <p className="text-emerald-600 text-xs mt-1">월 절감액 기준</p>
                  </div>

                  {/* Yearly Savings */}
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-4 md:p-6 border border-amber-200/50">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-3 md:mb-4">
                      <i className="ri-calendar-check-line text-xl md:text-2xl text-white"></i>
                    </div>
                    <p className="text-gray-600 text-xs md:text-sm mb-1">연간 예상 절감액</p>
                    <p className="text-lg md:text-2xl font-bold text-gray-900">
                      {formatNumber(yearlySavings)}<span className="text-sm md:text-lg font-medium text-gray-600">원</span>
                    </p>
                    <p className="text-amber-600 text-xs mt-1">월 절감액 × 12개월</p>
                  </div>
                </div>

                {/* 10년 총 절감액 */}
                <div className={`mt-4 md:mt-6 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-4 md:p-6 text-center transition-all duration-500 delay-300 ${animateResults ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <p className="text-amber-400 text-xs md:text-sm mb-2">SEPIS 수명 10년 기준 총 절감액</p>
                  <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-white">
                    {formatNumber(tenYearSavings)}<span className="text-lg md:text-xl text-gray-300 ml-1">원</span>
                  </p>
                </div>

                {/* CTA */}
                <div className={`mt-6 md:mt-10 text-center transition-all duration-500 delay-400 ${animateResults ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="bg-gray-50 rounded-xl p-4 md:p-6 border border-gray-200">
                    <p className="text-gray-600 mb-4 text-sm md:text-base">
                      <i className="ri-information-line text-amber-500 mr-1"></i>
                      정확한 절감액은 현장 진단을 통해 확인하실 수 있습니다.
                    </p>
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-2 px-6 md:px-8 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm md:text-base"
                    >
                      무료 현장 진단 신청하기
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isCalculated && (
              <div className="p-6 md:p-10 text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-3xl md:text-4xl text-amber-500"></i>
                </div>
                <p className="text-gray-500 text-sm md:text-base px-4">
                  전기요금과 계약전력 정보를 입력하고 <span className="text-amber-600 font-medium">절감액 계산하기</span> 버튼을 눌러주세요
                </p>
              </div>
            )}
          </div>

          {/* Bottom Info */}
          <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <i className="ri-shield-check-line text-amber-500"></i>
              <span className="text-xs md:text-sm">3년 무상 보증</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <i className="ri-time-line text-amber-500"></i>
              <span className="text-xs md:text-sm">10년 제품 수명</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <i className="ri-customer-service-2-line text-amber-500"></i>
              <span className="text-xs md:text-sm">전문 기술 지원</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SavingsCalculator;
